import React, { useState } from 'react';
import { Paper, Typography, Stack, Button } from '@mui/material';
import {
    CheckCircleOutline,
    HighlightOff,
    LocalShipping,
    DoneAll,
    Paid,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useApproveReturn } from '../../../hooks/returns/useApproveReturn';
import { useRejectReturn } from '../../../hooks/returns/useRejectReturn';
import { useReceiveReturn } from '../../../hooks/returns/useReceiveReturn';
import { useCompleteReturn } from '../../../hooks/returns/useCompleteReturn';
import { useRefundCompleteReturn } from '../../../hooks/returns/useRefundCompleteReturn';
import { useCancelReturn } from '../../../hooks/returns/useCancelReturn';

import ReturnActionDialog from './ReturnActionDialog';

// Map each status to the set of allowed transitions.
const ALLOWED_ACTIONS = {
    pending:           ['approve', 'reject', 'cancel'],
    approved:          ['receive', 'cancel'],
    awaiting_shipment: ['receive', 'cancel'],
    in_transit:        ['receive', 'cancel'],
    received:          ['complete', 'cancel'],
    completed:         ['refund-complete'],
    refunded:          [],
    rejected:          [],
    cancelled:         [],
};

const BUTTON_CONFIG = {
    approve: {
        labelAr: 'موافقة',
        labelEn: 'Approve',
        color: 'success',
        icon: CheckCircleOutline,
    },
    reject: {
        labelAr: 'رفض',
        labelEn: 'Reject',
        color: 'error',
        icon: HighlightOff,
    },
    receive: {
        labelAr: 'تأكيد الاستلام',
        labelEn: 'Mark Received',
        color: 'primary',
        icon: LocalShipping,
    },
    complete: {
        labelAr: 'إنهاء',
        labelEn: 'Complete',
        color: 'success',
        icon: DoneAll,
    },
    'refund-complete': {
        labelAr: 'تأكيد الاسترداد',
        labelEn: 'Mark Refund Complete',
        color: 'success',
        icon: Paid,
    },
    cancel: {
        labelAr: 'إلغاء',
        labelEn: 'Cancel',
        color: 'warning',
        icon: CancelIcon,
    },
};

const ReturnActionsPanel = ({ data, editableQuantities }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [openAction, setOpenAction] = useState(null);

    const { approveReturnMutate, isApproving } = useApproveReturn();
    const { rejectReturnMutate, isRejecting } = useRejectReturn();
    const { receiveReturnMutate, isReceiving } = useReceiveReturn();
    const { completeReturnMutate, isCompleting } = useCompleteReturn();
    const { refundCompleteReturnMutate, isRefundCompleting } = useRefundCompleteReturn();
    const { cancelReturnMutate, isCancelling } = useCancelReturn();

    const isSubmitting =
        isApproving || isRejecting || isReceiving || isCompleting || isRefundCompleting || isCancelling;

    const allowed = ALLOWED_ACTIONS[data?.status] || [];

    if (!data) return null;

    const handleSubmit = (payload) => {
        const id = data.id;
        const onDone = { onSuccess: () => setOpenAction(null) };

        switch (openAction) {
            case 'approve':
                approveReturnMutate({ id, data: payload }, onDone); break;
            case 'reject':
                rejectReturnMutate({ id, data: payload }, onDone); break;
            case 'receive':
                receiveReturnMutate({ id, data: payload || {} }, onDone); break;
            case 'complete':
                completeReturnMutate(id, onDone); break;
            case 'refund-complete':
                refundCompleteReturnMutate({ id, data: payload || {} }, onDone); break;
            case 'cancel':
                cancelReturnMutate({ id, data: payload || {} }, onDone); break;
            default:
                break;
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                position: { md: 'sticky' },
                top: { md: 16 },
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'الإجراءات' : 'Actions'}
            </Typography>

            {allowed.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {isArabic
                        ? 'لا توجد إجراءات متاحة للحالة الحالية.'
                        : 'No actions available for the current status.'}
                </Typography>
            ) : (
                <Stack spacing={1.25}>
                    {allowed.map((action) => {
                        const cfg = BUTTON_CONFIG[action];
                        const Icon = cfg.icon;
                        return (
                            <Button
                                key={action}
                                variant="contained"
                                color={cfg.color}
                                startIcon={<Icon />}
                                onClick={() => setOpenAction(action)}
                                disabled={isSubmitting}
                                fullWidth
                                sx={{ borderRadius: 2, py: 1.1, fontWeight: 'bold' }}
                            >
                                {isArabic ? cfg.labelAr : cfg.labelEn}
                            </Button>
                        );
                    })}
                </Stack>
            )}

            <ReturnActionDialog
                open={Boolean(openAction)}
                action={openAction}
                returnData={data}
                editableQuantities={editableQuantities}
                onClose={() => setOpenAction(null)}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
            />
        </Paper>
    );
};

export default ReturnActionsPanel;
